import { useEffect, useMemo, useState } from "react";
import {
  readBookmarkedPostIds,
  removeBookmarkedPost,
  upsertBookmarkedPost,
  writeBookmarkedPostIds,
} from "../bookmarks";

export default function BookmarkIconButton({
  post,
  categoryLabel,
  categorySlug,
  categoryLink,
  className = "",
  size = 44,
  iconSize = 20,
}) {
  const bookmarkId = useMemo(
    () => String(post?.__bookmarkId ?? post?.id ?? post?.slug ?? post?.link ?? ""),
    [post]
  );

  const [isBookmarked, setIsBookmarked] = useState(() =>
    bookmarkId ? readBookmarkedPostIds().includes(bookmarkId) : false
  );

  useEffect(() => {
    if (!bookmarkId) return;
    setIsBookmarked(readBookmarkedPostIds().includes(bookmarkId));
  }, [bookmarkId]);

  useEffect(() => {
    if (!bookmarkId) return;
    const sync = () => setIsBookmarked(readBookmarkedPostIds().includes(bookmarkId));
    window.addEventListener("storage", sync);
    window.addEventListener("pageshow", sync);
    window.addEventListener("focus", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("pageshow", sync);
      window.removeEventListener("focus", sync);
    };
  }, [bookmarkId]);

  const handleToggle = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!bookmarkId) return;

    const currentIds = readBookmarkedPostIds();
    const nextIds = currentIds.includes(bookmarkId)
      ? currentIds.filter((id) => id !== bookmarkId)
      : [...currentIds, bookmarkId];

    writeBookmarkedPostIds(nextIds);

    if (nextIds.includes(bookmarkId)) {
      upsertBookmarkedPost(post, { categoryLabel, categorySlug, categoryLink });
    } else {
      removeBookmarkedPost(bookmarkId);
    }

    setIsBookmarked(nextIds.includes(bookmarkId));
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
      aria-pressed={isBookmarked}
      className={`trw-bookmark-btn group flex items-center justify-center rounded-full border bg-white transition-colors hover:border-[#d02525] hover:text-[#d02525] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(247,13,40,0.2)] focus-visible:ring-offset-2 ${className} ${
        isBookmarked ? "border-[#d02525] text-[#d02525]" : "border-[#ccc] text-[#12172c]"
      } [.trw-dark-mode_&]:border-[#2a2d31] [.trw-dark-mode_&]:bg-[#151618] [.trw-dark-mode_&]:text-[#f87171]`}
      style={{ width: size, height: size }}
    >
      <svg
        width={iconSize}
        height={iconSize}
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="transition-colors"
        fill={isBookmarked ? "currentColor" : "none"}
      >
        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
      </svg>
    </button>
  );
}
